import React, { useId } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogHeader } from '../ui';

export interface BottomSheetProps {
  /** Whether the bottom sheet is open */
  open: boolean;
  /** Called when the bottom sheet should close */
  onClose: () => void;
  /** Optional title shown in the header */
  title?: string;
  /** Max width for desktop modal. Default: 'lg' */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** On mobile, make the sheet take the full screen (e.g., for camera) */
  fullScreenMobile?: boolean;
  /** Show the drag handle indicator. Default: true */
  showHandle?: boolean;
  /** Content */
  children: React.ReactNode;
}

const MAX_WIDTH_CLASSES: Record<string, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
};

/**
 * BottomSheet — the kit `Dialog` with a scrolling content area.
 *
 * - Phones: slides up from the bottom with a swipe-to-close handle, up to 90% of the screen.
 * - `sm` and up: floating panel over a blurred backdrop, limited by `maxWidth`.
 * - `fullScreenMobile`: fills the whole phone screen (e.g. camera views).
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  maxWidth = 'lg',
  fullScreenMobile = false,
  showHandle = true,
  children,
}) => {
  const titleId = useId();
  const handle = showHandle && !fullScreenMobile;

  const size = fullScreenMobile
    ? 'h-[100dvh] max-sm:rounded-t-none sm:h-auto sm:max-h-[84vh]'
    : 'max-h-[90dvh] sm:max-h-[84vh]';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={title ? titleId : undefined}
      label={title ? undefined : 'Dialog'}
      handle={handle}
      className={`${size} ${MAX_WIDTH_CLASSES[maxWidth] || MAX_WIDTH_CLASSES.lg}`}
    >
      {title ? (
        <DialogHeader
          id={titleId}
          title={title}
          onClose={onClose}
          className={`flex-shrink-0 pb-2 ${handle ? 'max-sm:pt-1' : ''}`}
        />
      ) : (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/85 text-neutral-600 ring-1 ring-black/[0.06] backdrop-blur transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
          style={fullScreenMobile ? { top: 'max(0.75rem, env(safe-area-inset-top))' } : undefined}
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-[13px] text-ink"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {children}
      </div>
    </Dialog>
  );
};
